import { resolveSchemaFields } from './schemaCompat';

const resolveKey = (field) => field.field_id || field.name;

export const evaluateCondition = (condition, formData) => {
  const { field_id, name, operator, value } = condition;
  const fieldValue = formData[field_id || name];

  switch (operator) {
    case 'equals':
      return fieldValue === value;
    case 'not_equals':
      // If the field has no value yet (undefined/null), don't treat it as "not equal"
      if (fieldValue === undefined || fieldValue === null) return false;
      return fieldValue !== value;
    case 'contains':
      return fieldValue && fieldValue.includes(value);
    case 'greater_than':
      return parseFloat(fieldValue) > parseFloat(value);
    case 'less_than':
      return parseFloat(fieldValue) < parseFloat(value);
    default:
      return true;
  }
};

export const shouldShowField = (field, formData) => {
  if (!field.conditional_display) return true;

  const { operator, conditions } = field.conditional_display;
  
  if (operator === 'AND') {
    return conditions.every(cond => evaluateCondition(cond, formData));
  } else if (operator === 'OR') {
    return conditions.some(cond => evaluateCondition(cond, formData));
  }
  
  return true;
};

export const validateField = (field, value) => {
  const validation = field.validation || {};
  
  if (field.required && (!value || value.toString().trim() === '')) {
    return `${field.label} is required`;
  }

  if (validation.min_length && value && value.length < validation.min_length) {
    return `${field.label} must be at least ${validation.min_length} characters`;
  }
  if (validation.max_length && value && value.length > validation.max_length) {
    return `${field.label} must be no more than ${validation.max_length} characters`;
  }

  if (validation.pattern && value) {
    const regex = new RegExp(validation.pattern);
    if (!regex.test(value)) {
      return validation.pattern_message || `${field.label} format is invalid`;
    }
  }

  return null;
};

const isGroupField = (field) => Array.isArray(field.fields) && field.fields.length > 0;

const hasValue = (value) =>
  value !== undefined && value !== null && value.toString().trim() !== '';

// Group values live on their child keys, never on the group's own key, so a
// required group is satisfied by its children: every visible required child
// must be filled, or at least one child when none is individually required.
const validateRequiredGroup = (group, formData, shouldShowFieldFn, newErrors) => {
  const children = group.fields.filter((child) => shouldShowFieldFn(child));
  const requiredChildren = children.filter((child) => child.required);
  if (requiredChildren.length === 0) {
    if (children.length > 0 && !children.some((child) => hasValue(formData[resolveKey(child)]))) {
      newErrors[resolveKey(group)] = `${group.label} is required`;
    }
    return;
  }
  requiredChildren.forEach((child) => {
    if (isGroupField(child)) {
      validateRequiredGroup(child, formData, shouldShowFieldFn, newErrors);
      return;
    }
    const key = resolveKey(child);
    const error = validateField(child, formData[key]);
    if (error) newErrors[key] = error;
  });
};

export const validateForm = (template, formData, shouldShowFieldFn) => {
  const newErrors = {};
  const fields = resolveSchemaFields(template.template_schema);

  fields.forEach(field => {
    if (shouldShowFieldFn(field)) {
      const key = resolveKey(field);
      if (isGroupField(field)) {
        if (field.required) validateRequiredGroup(field, formData, shouldShowFieldFn, newErrors);
        return;
      }
      const error = validateField(field, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    }
  });

  return newErrors;
};

export const validateRequiredTemplateFields = (template, formData, hiddenFieldTypes = []) => {
  const validationErrors = validateForm(
    template,
    formData,
    (field) =>
      shouldShowField(field, formData) && !hiddenFieldTypes.includes(field.type),
  );
  if (Object.keys(validationErrors).length === 0) return null;
  return Object.values(validationErrors).join(', ');
};
